<template>
  <div v-if="loading" class="d-flex justify-center mt-5">
    <v-progress-circular
      :size="200"
      :width="10"
      color="white"
      indeterminate
    ></v-progress-circular>
  </div>
  <v-container fluid grid-list-md v-else>
    <v-layout row wrap>
      <v-flex xs12 lg12>
        <ProspectHeader
          @displayFloatingWindows="displayFloatingWindows"
          @click="goToNextColumn"
          :isValid="!saving && prospectValid"
        />

        <v-card
          :class="{
            'main-panel-desktop': $mq !== 'xs' && $mq !== 'sm' && $mq !== 'md'
          }"
          class="mt-3"
        >
          <v-tabs v-model="leftTab" @change="tabChanged($event)" grow dark>
            <v-tab ripple>Infos</v-tab>
            <v-tab ripple v-if="isAdminOrSuperAdmin">Admin</v-tab>
            <v-tab-item>
              <ProspectInfos :edit-mode="editMode" v-if="leftTab === 0" />
            </v-tab-item>
            <v-tab-item v-if="isAdminOrSuperAdmin">
              <ProspectAdmin
                :prospect="prospect"
                :edit-mode="editMode"
                v-if="leftTab === 1"
              />
            </v-tab-item>
          </v-tabs>
        </v-card>
      </v-flex>
      <v-card class="floating-window">
        <v-tabs v-model="rightTab" grow>
          <v-tab ripple><v-icon>chat</v-icon></v-tab>
          <v-tab ripple><v-icon>timeline</v-icon></v-tab>
          <v-tab ripple><v-icon>attach_file</v-icon></v-tab>
          <v-tab-item>
            <Comments
              v-if="rightTab === 0"
              :target-id="prospect._id"
              :edit-mode="editMode"
            ></Comments>
          </v-tab-item>
          <v-tab-item>
            <Timeline
              v-if="rightTab === 1"
              :edit-mode="editMode"
              :target-id="prospect._id"
              :link-getter="linkGetter"
              :title-getter="titleGetter"
              :description-getter="descriptionGetter"
            ></Timeline>
          </v-tab-item>
          <v-tab-item>
            <drop-zone
              type="files"
              :value="files"
              edit-mode="true"
              :target="this.prospect._id"
              @add="refreshDocs"
              @remove="removeFile"
              class="mx-3 mt-3 mb-4"
            ></drop-zone>
          </v-tab-item>
        </v-tabs>
      </v-card>
    </v-layout>

    <v-snackbar v-model="showError" :timeout="7000"
      >Une erreur a eu lieu</v-snackbar
    >
  </v-container>
</template>
<script>
import * as _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import Comments from "../components/comments/Comments.vue";
import DropZone from "../components/commons/DropZone";
import ProspectAdmin from "../components/prospect/ProspectAdmin";
import ProspectHeader from "../components/prospect/ProspectHeader";
import ProspectInfos from "../components/prospect/ProspectInfos";
import Timeline from "../components/timeline/Timeline";
import {
  getTimelineDescriptions,
  getTimelineLinks,
  getTimelineTitles,
  getValidationRulesFromProgress
} from "../data/prospectData";
import { fetchHil } from "../utils";

export default {
  components: {
    DropZone,
    ProspectHeader,
    ProspectInfos,
    ProspectAdmin,
    Timeline,
    Comments
  },
  timers: {
    sync: { time: 1000, autostart: true, repeat: true }
  },
  data() {
    return {
      leftTab: 0,
      rightTab: 0,
      editMode: true,
      showError: false,
      loading: true,
      showSent: false,
      showRightOnMobile: false,
      saving: false,
      prospectValid: false,
      files: []
    };
  },

  async mounted() {
    await this.fetchProspect({ id: this.$route.params.id });
    this.refreshDocs();
    this.loading = false;
    if (this.$route.params.tab != null) {
      switch (this.$route.params.tab) {
        case "admin":
          this.leftTab = 1;
          break;
      }
    }
    window.addEventListener("beforeunload", this.sync);
    window.addEventListener("unload", this.sync);
  },

  beforeDestroy() {
    this.sync();
    window.removeEventListener("beforeunload", this.sync);
    window.removeEventListener("unload", this.sync);
  },

  methods: {
    ...mapActions("prospects", ["fetchProspect", "syncProspect"]),

    async removeFile(file) {
      await fetchHil(`/cache/${file._id}`, { method: "DELETE" });
      this.refreshDocs();
    },
    async refreshDocs() {
      const resp = await fetchHil(`/cache/for/${this.prospect._id}`);
      this.files = await resp.json();
    },

    displayFloatingWindows() {
      window.document.body.classList.add("display-floating-window");
    },

    hideFloatingWindows() {
      window.document.body.classList.remove("display-floating-window");
    },

    goToNextColumn(column) {
      this.prospect.currentProgress = column;
      this.prospectValid = false; // Set to invalid until the save happens
    },

    async isValid() {
      // Vee-validate doesn't want to deep validate nested components
      // Can't find out why, doing this for now...
      for (let k of Object.keys(this.validationRules)) {
        const { valid } = await this.$validator.verify(
          _.get(this.prospect, k),
          this.validationRules[k]
        );
        if (!valid) {
          this.prospectValid = false;
          return;
        }
      }
      this.prospectValid = true;
    },

    async sync() {
      if (this.loading) {
        return;
      }

      try {
        this.saving = true;
        await this.syncProspect();
      } catch (e) {
        console.error(e);
        this.showError = true;
      }

      await this.isValid();
      this.saving = false;
    },

    tabChanged(tab) {
      const pid = this.$route.params.id;
      switch (tab) {
        case 1:
          this.$router.replace(`/prospect/${pid}/admin`);
          break;
        default:
          this.$router.replace(`/prospect/${pid}`);
          break;
      }
    }
  },
  computed: {
    ...mapState("prospects", {
      prospect: state => state.current
    }),
    ...mapGetters("user", ["isAdminOrSuperAdmin"]),

    validationRules() {
      return getValidationRulesFromProgress(this.prospect.currentProgress);
    },

    titleGetter() {
      return getTimelineTitles;
    },

    linkGetter() {
      return getTimelineLinks;
    },

    descriptionGetter() {
      return getTimelineDescriptions;
    }
  }
};
</script>
<style scoped>
.v-tabs .v-window__container {
  height: auto !important;
}

.main-panel-desktop {
  margin-right: 410px;
}
</style>
<style>
.floating-window {
  z-index: 200;
  position: fixed;
  width: 400px;
  right: 20px;
}

@media screen and (max-width: 939px) {
  .floating-window {
    width: calc(100vw - 20px);
    right: 10px;
    left: 10px;
  }
}

@media screen and (max-width: 939px) {
  .floating-window,
  .floating-window-2 {
    display: none;
  }

  .display-floating-window .floating-window,
  .display-floating-window .floating-window-2 {
    display: block;
  }
}
</style>
